import { useEffect, useRef, useState } from 'react'
import { getAblyClient, MAIN_CHANNEL } from '../data/ablyClient.js'

/**
 * useAblyChannel — subscribe to the shared real-time channel.
 *
 * Whenever another client publishes a message matching `eventName`, the
 * provided `onMessage` handler fires. Also exposes a `publish` function
 * so mutations can broadcast.
 *
 * Typical usage:
 *   useAblyChannel('goals-changed', () => queryClient.invalidateQueries(['goals']))
 *
 * If Ably isn't configured, this hook is a no-op — publish() becomes
 * fire-and-forget and nothing is subscribed.
 */
export function useAblyChannel(eventName, onMessage) {
  const [connected, setConnected] = useState(false)
  const handlerRef = useRef(onMessage)
  handlerRef.current = onMessage

  useEffect(() => {
    let cancelled = false
    let cleanup = null

    ;(async () => {
      const client = await getAblyClient()
      if (!client || cancelled) return
      const channel = client.channels.get(MAIN_CHANNEL)
      const listener = (msg) => {
        // Ignore messages originated by this client (Ably echoes by default)
        if (msg.clientId && client.auth.clientId && msg.clientId === client.auth.clientId) {
          return
        }
        try {
          handlerRef.current?.(msg.data, msg)
        } catch (err) {
          console.warn('[ably] onMessage handler threw', err)
        }
      }
      await channel.subscribe(eventName, listener)
      setConnected(true)

      cleanup = () => {
        channel.unsubscribe(eventName, listener)
        setConnected(false)
      }
    })()

    return () => {
      cancelled = true
      if (cleanup) cleanup()
    }
  }, [eventName])

  return { connected }
}

/**
 * Publish a message to the shared channel. Fire-and-forget; if Ably isn't
 * available or the publish fails, we silently drop the message — the
 * server is still the source of truth, so other clients will see the
 * change on their next 15s poll.
 */
export async function publishAblyEvent(eventName, data) {
  try {
    const client = await getAblyClient()
    if (!client) return
    const channel = client.channels.get(MAIN_CHANNEL)
    await channel.publish(eventName, data || {})
  } catch (err) {
    console.warn('[ably] publish failed', eventName, err)
  }
}
